import { get, isFunction, isEmpty, includes } from 'lodash-es';
import { useEffect, useState } from 'react';
import { minMaxTimeout } from './minMaxTimeout';
import errorCodes from 'srcAlias/enums/errorCodes';
import { getLoginUrl } from './url';

export const useFetch = ({
  uri,
  fetchOptions = {},
  timeoutOptions = { min: 0, max: Infinity, clientOverallMax: Infinity },
  optionalDependencies = [],
  dependencies = [],
  successCondition,
}) => {
  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const uiTotalElapsedTimeout = setTimeout(() => {
      setError({ category: errorCodes.CLIENT_TIMEOUT });
      setLoading(false);
    }, timeoutOptions.clientOverallMax);

    return () => {
      clearTimeout(uiTotalElapsedTimeout);
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    const handleOtherError = (res) => {
      throw new Error(
        res.status === 408 ? errorCodes.NODE_TIMEOUT : errorCodes.NODE_GENERIC
      );
    };

    const handleResponse = async (res) => {
      const contentType = res.headers.get('content-type');
      const isJson = includes(contentType, 'application/json');
      const response = isJson ? await res.json() : await res.text();

      if (res.ok) {
        if (isFunction(successCondition) && !successCondition(response)) {
          doFetch();
        } else {
          setData(response);
          setLoading(false);
        }
      } else if (res.status === 401 || res.status === 403) {
        window.location.href = getLoginUrl(
          process.env.REACT_APP_DIAO_LOGIN_URL
        );
        setError({
          category: errorCodes.UNAUTHORIZED,
        });
      } else {
        handleOtherError(res);
      }
    };

    async function doFetch() {
      try {
        const [res] = await minMaxTimeout(
          fetch(uri, {
            credentials: 'include',
            ...fetchOptions,
            ...(isFunction(fetchOptions.headers)
              ? { headers: fetchOptions.headers() }
              : {}),
            signal: abortController.signal,
          }),
          timeoutOptions
        );
        await handleResponse(res);
      } catch (err) {
        if (get(err, 'name') === 'AbortError') {
          return;
        }
        setError({
          category: get(err, 'message') || errorCodes.CLIENT_GENERIC,
        });
        setLoading(false);
      }
    }

    const resolved = dependencies.every((dependency) => !isEmpty(dependency));

    if (resolved) {
      doFetch();
    }

    return () => {
      abortController.abort();
    };
  }, [uri, ...optionalDependencies, ...dependencies]);

  return { data, isLoading, error };
};
